import { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'

type SectionId = 'posts' | 'analytics' | 'settings'

const NAV_SECTIONS: { id: SectionId; label: string; items: { to: string; label: string }[] }[] = [
  {
    id: 'posts', label: 'Posts',
    items: [
      { to: '/composer', label: 'Composer' },
      { to: '/posts', label: 'Post History' },
    ],
  },
  {
    id: 'analytics', label: 'Analytics & Reports',
    items: [
      { to: '/analytics', label: 'Analytics' },
      { to: '/reports', label: 'Reports & ROI' },
      { to: '/followers', label: 'Followers' },
      { to: '/competitors', label: 'Competitors' },
      { to: '/listening', label: 'Listening' },
    ],
  },
  {
    id: 'settings', label: 'Settings',
    items: [
      { to: '/connections', label: 'Connections' },
    ],
  },
]

const DIRECT_LINKS: { to: string; label: string }[] = [
  { to: '/inbox', label: 'Inbox' },
  { to: '/advertising', label: 'Advertising' },
]

function useIsDesktop() {
  const mq = useRef(window.matchMedia('(min-width: 769px)'))
  const [desktop, setDesktop] = useState(mq.current.matches)
  useEffect(() => {
    const handler = (e: MediaQueryListEvent) => setDesktop(e.matches)
    mq.current.addEventListener('change', handler)
    return () => mq.current.removeEventListener('change', handler)
  }, [])
  return desktop
}

export default function Layout() {
  const [openSection, setOpenSection] = useState<SectionId | null>(null)
  const isDesktop = useIsDesktop()
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const prevPath = useRef(location.pathname)

  const sidebarOpen = isDesktop || mobileSidebarOpen

  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      setMobileSidebarOpen(false)
      prevPath.current = location.pathname
    }
  }, [location.pathname])

  const toggleSection = (id: SectionId) => {
    setOpenSection(prev => prev === id ? null : id)
  }

  const isChildActive = (items: { to: string }[]) =>
    items.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'))

  const handleLogout = () => {
    localStorage.removeItem('smm_token')
    window.dispatchEvent(new Event('storage'))
    navigate('/login', { replace: true })
  }

  return (
    <div className={`smm-layout${isDesktop ? ' smm-layout-desktop' : ''}`}>
      {!isDesktop && mobileSidebarOpen && <div className="smm-sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} />}

      <nav className={`smm-sidebar${sidebarOpen ? ' smm-sidebar-open' : ''}`}>
        <div className="smm-sidebar-compact-header">
          <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--smm-sidebar-text, #e0e0e0)' }}>
            Social Sandbox
          </span>
        </div>
        <ul className="smm-nav">
          {NAV_SECTIONS.map(section => (
            <li key={section.id} className="smm-nav-section">
              <button
                className={`smm-nav-header${openSection === section.id ? ' smm-nav-header-open' : ''}${isChildActive(section.items) ? ' smm-nav-header-active' : ''}`}
                onClick={() => toggleSection(section.id)}
              >
                <span className="smm-nav-header-icon">{openSection === section.id ? '\u2212' : '+'}</span>
                {section.label}
              </button>
              {openSection === section.id && (
                <ul className="smm-nav-sub">
                  {section.items.map(item => (
                    <li key={item.to}><NavLink to={item.to}>{item.label}</NavLink></li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {DIRECT_LINKS.map(link => (
            <li key={link.to} className="smm-nav-section">
              <NavLink to={link.to} className={({ isActive }) =>
                `smm-nav-header${isActive ? ' smm-nav-header-active' : ''}`
              } style={{ display: 'block', textDecoration: 'none' }}>
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 'auto', padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: 'var(--smm-sidebar-text, #e0e0e0)',
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, transition: 'background 0.2s',
            }}
            onMouseEnter={e => (e.target as HTMLButtonElement).style.background = 'rgba(255,255,255,0.15)'}
            onMouseLeave={e => (e.target as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)'}
          >
            Sign Out
          </button>
        </div>
      </nav>
      <main className="smm-main">
        {!isDesktop && (
          <div className="smm-main-topbar">
            <button className="smm-sidebar-toggle" onClick={() => setMobileSidebarOpen(v => !v)} aria-label="Toggle sidebar">
              {mobileSidebarOpen ? '\u2715' : '\u2630'}
            </button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  )
}
